import createClient, { type FetchResponse, type Middleware } from "openapi-fetch";

import { etagFetch } from "./etag-fetch";
import type { components, paths } from "./schema";

export type { components, paths } from "./schema";
export type Schemas = components["schemas"];

/** `web/openapi.json` paths already carry this prefix; the client is same-origin. */
export const API_PREFIX = "/api/v1";

export type ErrorBody = Schemas["ErrorBody"];
export type ErrorCode =
  | "bad_request"
  | "not_found"
  | "method_not_allowed"
  | "conflict"
  | "payload_too_large"
  | "range_not_satisfiable"
  | "internal_error"
  | "network_error"
  | (string & {});

/** Every non-2xx answer (and a failed fetch) becomes one of these. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly detail: unknown;

  constructor(status: number, code: ErrorCode, message: string, detail: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

function isErrorBody(value: unknown): value is ErrorBody {
  if (!value || typeof value !== "object") return false;
  const error = (value as { error?: unknown }).error;
  return (
    !!error &&
    typeof error === "object" &&
    typeof (error as { code?: unknown }).code === "string" &&
    typeof (error as { message?: unknown }).message === "string"
  );
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: "The request was not valid.",
  401: "Your session expired. Sign in again.",
  403: "You do not have access to this.",
  404: "Not found.",
  409: "This conflicts with the run's current state.",
  413: "The file is too large.",
  500: "The server hit a problem.",
};

/** Build an `ApiError` from a non-2xx response and whatever body openapi-fetch parsed. */
export function toApiError(response: Response, body: unknown): ApiError {
  if (isErrorBody(body)) {
    return new ApiError(response.status, body.error.code, body.error.message, body.error.detail);
  }
  const message =
    typeof body === "string" && body.trim() !== ""
      ? body.trim().slice(0, 200)
      : (FALLBACK_MESSAGES[response.status] ?? `Request failed (${response.status}).`);
  return new ApiError(response.status, `http_${response.status}`, message, body ?? null);
}

/** Read a Response's error body (JSON or text) and build the `ApiError`. */
export async function parseErrorResponse(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    const text = await response.text();
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }
  } catch {
    body = null;
  }
  return toApiError(response, body);
}

/** True for any error thrown by the client. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** A readable message for banners and toasts. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export interface TokenOptions {
  /** Skip the cached token and mint a fresh one (the 401 retry). */
  refresh?: boolean;
}

export type TokenGetter = (options?: TokenOptions) => Promise<string | null> | string | null;

// A no-op until the auth provider installs Clerk's getter. The token is attached to every API
// request because the production frontend and backend use different subdomains, so Clerk's
// host-only session cookie is not sent to the API host.
let getToken: TokenGetter = () => null;

/** Install the function that supplies the Bearer token (S24). Pass `null` to remove it. */
export function setTokenGetter(getter: TokenGetter | null): void {
  getToken = getter ?? (() => null);
}

/**
 * The current Bearer token (null when none is installed). For requests that cannot go through
 * the openapi-fetch client, such as the upload's XMLHttpRequest, which needs progress events.
 */
export async function authToken(options?: TokenOptions): Promise<string | null> {
  return (await getToken(options)) || null;
}

export interface AuthHandlers {
  /** A request stayed 401 after one token refresh: the session is gone; route to sign-in. */
  onUnauthorized?: () => void;
  /** A 403: the session has no active (or the wrong) organization; show the org gate. */
  onForbidden?: () => void;
}

let authHandlers: AuthHandlers = {};

/** Install what the client does after a failed 401 retry or a 403 (S24). `null` removes them. */
export function setAuthHandlers(handlers: AuthHandlers | null): void {
  authHandlers = handlers ?? {};
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** True when the 401 retry should be attempted: a token could be minted at all. */
async function freshToken(): Promise<string | null> {
  try {
    return (await getToken({ refresh: true })) || null;
  } catch {
    return null;
  }
}

/** The request's body as the retry can send it (the original stream was consumed by fetch). */
const retryBodies = new Map<string, Promise<ArrayBuffer | null>>();

const authMiddleware: Middleware = {
  async onRequest({ request, id }) {
    // Keep a copy of a mutating body so the 401 retry can resend it.
    if (MUTATING.has(request.method)) {
      retryBodies.set(id, request.body ? request.clone().arrayBuffer() : Promise.resolve(null));
    }
    let token: string | null = null;
    try {
      token = (await getToken()) || null;
    } catch {
      // The response handler will try once more with a refreshed token and route to sign-in if
      // Clerk cannot provide one.
    }
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response, id, options }) {
    const body = retryBodies.get(id);
    retryBodies.delete(id);
    if (response.status === 403) {
      authHandlers.onForbidden?.();
      return undefined;
    }
    if (response.status !== 401) return undefined;
    // One retry with a freshly minted token (a stale cached token, or a cookie that ClerkJS
    // has not refreshed yet on a GET); still 401 → the session is gone.
    const token = await freshToken();
    if (!token) {
      authHandlers.onUnauthorized?.();
      return undefined;
    }
    const headers = new Headers(request.headers);
    headers.set("Authorization", `Bearer ${token}`);
    const retry = new Request(request.url, {
      method: request.method,
      headers,
      body: body ? await body : null,
      credentials: request.credentials,
    });
    const fetchImpl = options.fetch ?? globalThis.fetch;
    const retried = await fetchImpl(retry);
    if (retried.status === 401) authHandlers.onUnauthorized?.();
    else if (retried.status === 403) authHandlers.onForbidden?.();
    return retried;
  },
  onError({ id }) {
    retryBodies.delete(id);
    return undefined;
  },
};

/**
 * The API origin: the page's own (the SPA is same-origin with FastAPI, and Vite proxies `/api`
 * in dev). Absolute because `Request` rejects relative URLs outside a browser (tests).
 */
export function apiBaseUrl(): string {
  return typeof window !== "undefined" && window.location?.origin !== "null"
    ? window.location.origin
    : "http://127.0.0.1:8000";
}

/** The typed openapi-fetch client. Prefer the query hooks in `./queries`. */
export const api = createClient<paths>({ baseUrl: apiBaseUrl(), fetch: etagFetch });
api.use(authMiddleware);

type AnyResponse = FetchResponse<
  Record<string, unknown>,
  Record<string, unknown>,
  `${string}/${string}`
>;

/**
 * Turn an openapi-fetch result into data-or-throw: non-2xx becomes `ApiError`, and a thrown
 * `TypeError` (network failure) becomes `ApiError(0, "network_error")`.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, "network_error", "Could not reach the server.", error);
  }
  const { data, error, response } = result;
  if (!response.ok) throw toApiError(response, error ?? data);
  if (data === undefined) {
    // A 2xx with no JSON body (204, or a media response); callers that expect data must not
    // use unwrap for those.
    throw toApiError(response, null);
  }
  return data;
}

export type { AnyResponse };

/** Absolute (same-origin) URL of a run media file: `/api/v1/runs/{id}/media/{path}`. */
export function mediaUrl(runId: string, path: string, organizationId?: string | null): string {
  const safePath = path
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  const url = `${API_PREFIX}/runs/${encodeURIComponent(runId)}/media/${safePath}`;
  return organizationId ? `${url}?org=${encodeURIComponent(organizationId)}` : url;
}
